import React, { FC, useCallback } from 'react'
import { OrgSegmentModel } from '../../../models/segments/org-segment.model'
import { AsyncButton } from '../../async-button/async-button.component'
import { SegmentModel } from '../../../models/segments/segment.model'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService } from '../../../services'
import { SegmentService } from '../../../services/segment'
import { OrgSegmentService } from '../../../services/org-segment'

type SegmentRowActionsProps = {
    level: 'org' | 'domain'
    segment: OrgSegmentModel | SegmentModel
    canMutate: boolean
    canEditOrgSegment: boolean
    currentUserHasWriteAccess: boolean
    hideRowActions: boolean
    onArchived(): void | Promise<void>
}

const useSegmentRouter = (level: 'org' | 'domain') => {
    const appSvc = useService(AppService)

    return useCallback(
        (path: string | number, buildOnly: boolean = false) => {
            return appSvc.routeWithin(level, `/segments/${path}`, buildOnly)
        },
        [level],
    )
}

export const SegmentRowActions: FC<SegmentRowActionsProps> = (props) => {
    const { level, segment, canMutate, canEditOrgSegment, currentUserHasWriteAccess, hideRowActions, onArchived } =
        props

    const segmentSvc = useService(SegmentService)
    const orgSegmentSvc = useService(OrgSegmentService)

    const route = useSegmentRouter(level)

    const segmentId = segment.getId()
    const detailsRoutePath = `${segmentId}/summary`
    const editRoutePath = `${segmentId}`
    const duplicateRoutePath = `new?template=${segmentId}`

    const handleArchiveClick = useCallback(async () => {
        let resolver: Promise<{ ok: boolean; error?: any } | { ok: false }>
        if (level === 'domain' && segment instanceof SegmentModel) {
            resolver = segmentSvc
                .deleteSegmentById(segment.getDomainId(), segment.getId())
                .then((ok) => ({ ok }))
                .catch((error) => ({ ok: false, error }))
        } else if (level === 'org' && segment instanceof OrgSegmentModel) {
            resolver = orgSegmentSvc.archive(segment.getAccountId(), {
                id: segment.getId(),
                name: segment.getName(),
            })
        } else {
            resolver = Promise.resolve({ ok: false })
        }

        const res = await Promise.resolve(resolver)
        if (res.ok) {
            onArchived()
        }
    }, [level, segmentId])

    let actions: any = []

    if (canMutate) {
        if (level === 'domain') {
            actions.push({
                text: 'Edit',
                icon: 'edit',
                onClick: () => route(editRoutePath),
                altHref: route(editRoutePath, true),
            })
        }

        actions.push({
            text: 'Duplicate',
            icon: 'copy',
            onClick: () => route(duplicateRoutePath),
            altHref: route(duplicateRoutePath, true),
        })

        // multi-domain segments cannot be archived
        // from the domain level list view
        const canArchive = level === 'org' || !segment.getIsMultiDomain()
        const baseArchiveAction = {
            text: 'Archive',
            icon: 'folder',
        }

        if (canArchive) {
            if (level !== 'org' || canEditOrgSegment) {
                const showAddtOrgCopy =
                    level === 'org' &&
                    (segment as OrgSegmentModel).getIsMultiDomain() &&
                    (segment as OrgSegmentModel).getSegments().length > 1

                actions.push(
                    {
                        divider: true,
                    },
                    {
                        ...baseArchiveAction,
                        onClick: handleArchiveClick,
                        confirm: {
                            okText: 'Yes, Archive',
                            title: 'Archive Segment',
                            content: (
                                <div className="archive-segment-confirm">
                                    Are you sure you want to archive the following segment?
                                    <div
                                        className={`archive-segment-confirm-details ${
                                            showAddtOrgCopy ? 'level-org' : ''
                                        }`}
                                    >
                                        <p>
                                            <b>{segment.getName()}</b>
                                        </p>
                                        <p>
                                            This segment will no longer be available to target during notification
                                            creation.
                                        </p>
                                    </div>
                                    {showAddtOrgCopy && (
                                        <div className="archive-segment-confirm-multi-domain">
                                            <p>
                                                This action will also remove the ability for the segment to be targeted
                                                on the following domains:
                                            </p>

                                            <div className="archive-segment-confirm-multi-domain-list">
                                                <ul>
                                                    {(segment as OrgSegmentModel).getSegments().map((s) => (
                                                        <li key={s.id}>{s.domain_display_name}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ),
                        },
                    },
                )
            }
        } else {
            currentUserHasWriteAccess &&
                actions.push({
                    ...baseArchiveAction,
                    onClick: () => true,
                    confirm: {
                        title: 'Multi-Domain Segment',
                        okText: 'Close',
                        cancelButtonProps: {
                            className: 'display-none',
                        },
                        content: (
                            <div>
                                This is a multi-domain segment and can only be archived from the organization's
                                multi-domain segments page.
                            </div>
                        ),
                    },
                })
        }
    }

    if (hideRowActions) {
        actions = undefined
    }

    if (!currentUserHasWriteAccess) {
        actions = undefined
    }

    return level === 'org' ? (
        <AsyncButton
            actions={actions}
            onClick={() => route(editRoutePath)}
            size="small"
            altHref={route(editRoutePath, true)}
            disabled={!canEditOrgSegment}
            disabledTooltip="You do not have sufficient permissions to edit this segment"
        >
            <span>Edit</span>
        </AsyncButton>
    ) : level === 'domain' ? (
        <AsyncButton
            actions={actions}
            onClick={() => route(detailsRoutePath)}
            size="small"
            altHref={route(detailsRoutePath, true)}
        >
            <span>View</span>
        </AsyncButton>
    ) : null
}
